import { useCallback, useEffect, useRef, useState } from "react";

import conversationRuntimeService from "../services/conversationRuntimeService";
import botService from "src/features/Bots/services/botservice";

function useConversationRuntime(conversation) {
  const conversationId = conversation?.id;
  const conversationWorkspaceId = conversation?.workspace_id;
  const activeConversation = useRef(conversationId);
  activeConversation.current = conversationId;
  const [runtime, setRuntime] = useState(null);
  const [runtimeHistory, setRuntimeHistory] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [bots, setBots] = useState([]);
  const [users, setUsers] = useState([]);
  const [canAssign, setCanAssign] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [labels, setLabels] = useState([]);
  const [note] = useState("");
  const [loading, setLoading] = useState(false);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeHistoryLoading, setRuntimeHistoryLoading] = useState(false);
  const [conversationHistoryLoading, setConversationHistoryLoading] =
    useState(false);
  const [botsLoading, setBotsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const loadLabels = useCallback(() => {
    if (!conversationId) {
      setLabels([]);
      return;
    }
    const contactLabels =
      conversation?.contact?.labels || conversation?.labels || [];
    setLabels(Array.isArray(contactLabels) ? contactLabels : []);
  }, [conversationId, conversation]);

  const addLabel = useCallback(async (labelName) => {
    const value = String(labelName || "").trim();
    if (!value) return;

    setLabels((prev) => {
      const exists = prev.some((label) => {
        const name =
          typeof label === "string"
            ? label
            : label?.name || label?.label || label?.value;
        return String(name || "").toLowerCase() === value.toLowerCase();
      });

      if (exists) return prev;

      return [
        ...prev,
        {
          id: `local-${Date.now()}`,
          name: value,
        },
      ];
    });
  }, []);

  const removeLabel = useCallback(async (label) => {
    setLabels((prev) =>
      prev.filter((item) => {
        if (item === label) {
          return false;
        }

        if (item?.id && label?.id && item.id === label.id) {
          return false;
        }

        const itemName =
          typeof item === "string"
            ? item
            : item?.name || item?.label || item?.value;
        const labelName =
          typeof label === "string"
            ? label
            : label?.name || label?.label || label?.value;
        return itemName !== labelName;
      }),
    );
  }, []);

  const loadRuntime = useCallback(async () => {
    if (!conversationId) {
      setRuntime(null);
      return;
    }

    try {
      setRuntimeLoading(true);
      const response = await conversationRuntimeService.runtime(conversationId);
      const data = response?.data?.data ?? response?.data ?? null;
      setRuntime(data);
    } catch (error) {
      console.error("Failed to load conversation runtime.", error);
      setRuntime(null);
    } finally {
      setRuntimeLoading(false);
    }
  }, [conversationId]);

  const loadRuntimeHistory = useCallback(async () => {
    if (!conversationId) {
      setRuntimeHistory([]);
      return;
    }
    try {
      setRuntimeHistoryLoading(true);
      const response = await conversationRuntimeService.history(conversationId);
      const records = response?.data?.data ?? response?.data ?? [];
      setRuntimeHistory(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Failed to load runtime history.", error);
      setRuntimeHistory([]);
    } finally {
      setRuntimeHistoryLoading(false);
    }
  }, [conversationId]);

  const loadConversationHistory = useCallback(async () => {
    if (!conversationId) {
      setConversationHistory([]);
      return;
    }

    try {
      setConversationHistoryLoading(true);
      const response =
        await conversationRuntimeService.conversationHistory(conversationId);
      const records = response?.data?.data ?? response?.data ?? [];
      setConversationHistory(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Failed to load conversation history.", error);
      setConversationHistory([]);
    } finally {
      setConversationHistoryLoading(false);
    }
  }, [conversationId]);

  const loadBots = useCallback(async () => {
    let setup = {};
    try {
      setup = JSON.parse(localStorage.getItem("whatsapp_setup") || "{}");
    } catch (error) {
      console.error("Failed to parse whatsapp_setup.", error);
    }

    const workspaceId = setup?.workspaceId;
    if (!workspaceId) {
      console.warn("Workspace ID not found in whatsapp_setup.");
      setBots([]);
      return;
    }
    try {
      setBotsLoading(true);
      const response = await botService.index(workspaceId);
      const records = response?.data?.data ?? response?.data ?? [];
      setBots(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Failed to load WhatsApp bots.", error);
      setBots([]);
    } finally {
      setBotsLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setCanAssign(false);
    setUsers([]);
    setUsersError("");
    setUsersLoading(true);
    try {
      let cached = {};
      try {
        cached = JSON.parse(localStorage.getItem("whatsapp_setup") || "{}");
      } catch {
        /* Ignore invalid cache. */
      }
      const workspaceId = conversationWorkspaceId || cached?.workspaceId;
      if (!workspaceId) return;
      const response = await conversationRuntimeService.loadUser(workspaceId);
      if (activeConversation.current === conversationId) {
        setUsers(response.data?.data || []);
        setCanAssign(
          response.data?.capabilities?.can_assign_conversations === true,
        );
      }
    } catch (error) {
      if (activeConversation.current === conversationId)
        setUsersError(
          "Could not load the organisation team. Reopen this conversation to retry.",
        );
    } finally {
      if (activeConversation.current === conversationId) setUsersLoading(false);
    }
  }, [conversationId, conversationWorkspaceId]);

  const loadConversationRuntime = useCallback(async () => {
    if (!conversationId) {
      setRuntime(null);
      setRuntimeHistory([]);
      setConversationHistory([]);
      setLabels([]);
      return;
    }

    setLoading(true);

    try {
      await Promise.all([
        loadRuntime(),
        loadRuntimeHistory(),
        loadConversationHistory(),
        loadBots(),
        loadUsers(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [
    conversationId,
    loadRuntime,
    loadRuntimeHistory,
    loadConversationHistory,
    loadBots,
    loadUsers,
  ]);

  // Summary refreshes create a new conversation object. Updating its labels
  // must not refetch every runtime, history, team and bot resource again.
  useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  useEffect(() => {
    loadConversationRuntime();
  }, [loadConversationRuntime]);

  const refreshHistories = useCallback(async () => {
    await Promise.all([loadRuntimeHistory(), loadConversationHistory()]);
  }, [loadRuntimeHistory, loadConversationHistory]);

  const assignUser = useCallback(
    async (user) => {
      if (!conversationId) {
        return;
      }
      try {
        setActionLoading("assign-user");
        const response = await conversationRuntimeService.assignUser(
          conversationId,
          {
            assigned_user_id: user?.id || null,
          },
        );

        await Promise.all([loadRuntime(), refreshHistories()]);
        return response;
      } catch (error) {
        console.error("Failed to assign user.", error);
        throw error;
      } finally {
        setActionLoading(null);
      }
    },
    [conversationId, loadRuntime, refreshHistories],
  );

  const changeBot = useCallback(
    async (bot) => {
      if (!conversationId || !bot?.id) {
        return;
      }

      try {
        setActionLoading("assign-bot");
        const response = await conversationRuntimeService.assignBot(
          conversationId,
          {
            whatsapp_bot_id: bot.id,
          },
        );

        await Promise.all([loadRuntime(), refreshHistories()]);
        return response;
      } catch (error) {
        console.error("Failed to assign bot.", error);
        throw error;
      } finally {
        setActionLoading(null);
      }
    },
    [conversationId, loadRuntime, refreshHistories],
  );

  const changeMode = useCallback(
    async (mode) => {
      if (!conversationId || !mode) {
        return;
      }
      try {
        setActionLoading("change-mode");
        const response = await conversationRuntimeService.changeMode(
          conversationId,
          {
            mode,
          },
        );

        await Promise.all([loadRuntime(), refreshHistories()]);
        return response;
      } catch (error) {
        console.error("Failed to change conversation mode.", error);
        throw error;
      } finally {
        setActionLoading(null);
      }
    },
    [conversationId, loadRuntime, refreshHistories],
  );

  const pause = useCallback(async () => {
    if (!conversationId) {
      return;
    }
    try {
      setActionLoading("pause");
      const pausedUntil = new Date(Date.now() + 30 * 60 * 1000)
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      const payload = {
        paused_until: pausedUntil,
      };
      const response = await conversationRuntimeService.pause(
        conversationId,
        payload,
      );

      await Promise.all([loadRuntime(), refreshHistories()]);
      return response;
    } catch (error) {
      console.error("Failed to pause conversation.", error);
      throw error;
    } finally {
      setActionLoading(null);
    }
  }, [conversationId, loadRuntime, refreshHistories]);

  const resume = useCallback(async () => {
    if (!conversationId) {
      return;
    }
    try {
      setActionLoading("resume");
      const response = await conversationRuntimeService.resume(conversationId);
      await Promise.all([loadRuntime(), refreshHistories()]);
      return response;
    } catch (error) {
      console.error("Failed to resume conversation.", error);
      throw error;
    } finally {
      setActionLoading(null);
    }
  }, [conversationId, loadRuntime, refreshHistories]);

  const reset = useCallback(async () => {
    if (!conversationId) {
      return;
    }
    try {
      setActionLoading("reset");
      const response = await conversationRuntimeService.reset(conversationId);
      await Promise.all([loadRuntime(), refreshHistories()]);
      return response;
    } catch (error) {
      console.error("Failed to reset conversation.", error);
      throw error;
    } finally {
      setActionLoading(null);
    }
  }, [conversationId, loadRuntime, refreshHistories]);

  const restart = useCallback(async () => {
    if (!conversationId) {
      return;
    }
    try {
      setActionLoading("restart");
      const response = await conversationRuntimeService.restart(conversationId);
      await Promise.all([loadRuntime(), refreshHistories()]);
      return response;
    } catch (error) {
      console.error("Failed to restart conversation.", error);
      throw error;
    } finally {
      setActionLoading(null);
    }
  }, [conversationId, loadRuntime, refreshHistories]);

  const closeConversation = useCallback(async () => {
    if (!conversationId) {
      return;
    }
    try {
      setActionLoading("close");
      const response = await conversationRuntimeService.close(conversationId);
      await Promise.all([loadRuntime(), refreshHistories()]);
      return response;
    } catch (error) {
      console.error("Failed to close conversation.", error);
      throw error;
    } finally {
      setActionLoading(null);
    }
  }, [conversationId, loadRuntime, refreshHistories]);

  const mode = runtime?.mode || runtime?.conversation_mode || "Bot";
  const selectedBot =
    runtime?.bot || runtime?.whatsapp_bot || runtime?.whatsappBot || null;
  const assignedUser = runtime
    ? runtime.user || runtime.assigned_user || runtime.assignedUser || null
    : conversation?.assigned_user || null;
  const currentStage = runtime?.current_stage || runtime?.stage || null;
  const runtimeStatus = runtime?.status || conversation?.status || "ACTIVE";

  const refresh = useCallback(async () => {
    await Promise.all([loadRuntime(), refreshHistories()]);
  }, [loadRuntime, refreshHistories]);

  return {
    runtime,
    mode,
    selectedBot,
    assignedUser,
    currentStage,
    runtimeStatus,
    bots,
    users,
    runtimeHistory,
    conversationHistory,
    loading,
    runtimeLoading,
    runtimeHistoryLoading,
    conversationHistoryLoading,
    botsLoading,
    usersLoading,
    usersError,
    canAssign,
    actionLoading,
    assignUser,
    changeBot,
    changeMode,
    pause,
    resume,
    reset,
    restart,
    closeConversation,
    labels,
    note,
    addLabel,
    removeLabel,
    refresh,
    refreshHistories,
    loadRuntime,
    loadRuntimeHistory,
    loadConversationHistory,
  };
}

export default useConversationRuntime;
